import React, { ChangeEvent, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { ApiError, downloadCustomerImportTemplate, importCustomersFile } from './customerCenterApi';
import type { BranchId, CustomerImportResult } from './types';

interface Props {
  show: boolean;
  branchId: BranchId;
  onClose: () => void;
  onImported: () => void;
}

const ImportCustomersModal: React.FC<Props> = ({ show, branchId, onClose, onImported }) => {
  const { t } = useTranslation('scenes.customerCenter');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CustomerImportResult | null>(null);

  const reset = () => {
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    setBusy(true);
    try {
      const blob = await downloadCustomerImportTemplate(branchId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'RevRex_Customer_Import.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('import.failed');
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const importResult = await importCustomersFile(branchId, file);
      setResult(importResult);
      if (importResult.imported > 0) onImported();
      toast.success(t('import.doneToast', { imported: importResult.imported, failed: importResult.failed }));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : t('import.failed');
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered scrollable data-testid="customer-import-modal">
      <Modal.Header closeButton>
        <Modal.Title>{t('import.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted">{t('import.help')}</p>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button variant="outline-primary" disabled={busy} onClick={handleDownloadTemplate}>
            {t('import.downloadTemplate')}
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => fileRef.current?.click()}>
            {t('import.upload')}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            className="d-none"
            onChange={handleFileChange}
          />
        </div>
        {result ? (
          <div data-testid="customer-import-result">
            <p className="fw-semibold">{t('import.summary', { imported: result.imported, failed: result.failed })}</p>
            {result.errors.length ? (
              <Table striped bordered size="sm" responsive>
                <thead>
                  <tr>
                    <th>{t('import.colRow')}</th>
                    <th>{t('import.colCustomer')}</th>
                    <th>{t('import.colField')}</th>
                    <th>{t('import.colMessage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((row, index) => (
                    <tr key={`${row.row}-${index}`}>
                      <td>{row.row}</td>
                      <td>{row.customer_name}</td>
                      <td>{row.field}</td>
                      <td>{row.message}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>{t('form.cancel')}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportCustomersModal;
